import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import * as XLSX from "xlsx";
import {
  loadAppSettingsHybrid,
  listenRelationRowsHybrid,
  addRelationRow,
  updateRelationRow,
  deleteRelationRow,
  addRelationRowsBatch,
  migrateRelationsSettingDocToCollection,
  getRelationRowsPage,
} from "../services/data";
// Helpers d'affichage/tri
const safeLabel = (f) => f?.name || f?.label || f?.key || "";
const byOrderThenLabel = (a, b) =>
  Number(a?.order ?? 0) - Number(b?.order ?? 0) ||
  safeLabel(a).localeCompare(safeLabel(b));

export default function RelationPage({ typeKey = "client", user }) {
  const [view, setView] = useState("list");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [olderRows, setOlderRows] = useState([]);
  const [relCursor, setRelCursor] = useState(null);
  const [isMoreRelLoading, setIsMoreRelLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [relationTypes, setRelationTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const fileInputRef = useRef(null);
  // [down] tri: 'desc' = plus récent d'abord (par défaut)
  const [sortOrder, setSortOrder] = useState("desc");
  const loadMoreRelations = useCallback(async () => {
    if (!user?.uid || !relCursor) return;
    setIsMoreRelLoading(true);
    const { items, cursor } = await getRelationRowsPage(user.uid, typeKey, {
      afterDoc: relCursor,
      pageSize: 50,
    });
    setRelCursor(cursor);
    setOlderRows((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      items.forEach((x) => {
        if (!map.has(x.id)) map.set(x.id, x);
      });
      return Array.from(map.values());
    });
    setIsMoreRelLoading(false);
  }, [user?.uid, typeKey, relCursor]);
  // [down] util: extraire un timestamp robuste pour trier
  function rowToTs(row, idx) {
    const toMs = (v) => {
      if (v == null || v === "") return 0;
      if (typeof v === "number") return v;
      if (typeof v === "object") {
        if (typeof v.toDate === "function") return +v.toDate();
        if (typeof v.seconds === "number")
          return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
      }
      const t = Date.parse(v);
      return Number.isNaN(t) ? 0 : t;
    };
    const cands = [row.updatedAt, row.createdAt];
    for (const c of cands) {
      const ms = toMs(c);
      if (ms) return ms;
    }
    const dateField = applicableFields.find(
      (f) => f.type === "date" || /date|created|updated/i.test(f.key)
    );
    if (dateField) {
      const ms = toMs(row[dateField.key]);
      if (ms) return ms;
    }
    return idx;
  }
  // [fire] Charger la configuration depuis Firestore
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = loadAppSettingsHybrid(user.uid, (app) => {
      const data = app?.relations || {};
      setRelationTypes(data.types || []);
      setCustomFields(data.fields || []);
    });
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid]);
  useEffect(() => {
    if (!user?.uid || !typeKey) return;
    // migre les anciennes données (si présentes) vers la collection
    migrateRelationsSettingDocToCollection(user.uid, typeKey, {
      clearOld: true,
    })
      .then(({ moved }) => {
        if (moved)
          console.log(`[repeat] Migration ${typeKey}: ${moved} lignes déplacées`);
      })
      .catch((e) =>
        console.warn(
          "Migration relations_data -> collection échouée:",
          e?.message || e
        )
      );
    // aucune dépendance volontaire (on ne relance pas en boucle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, typeKey]);

  // Trouver le type courant
  const currentType = relationTypes.find((t) => t.key === typeKey) || {
    label: "Relations",
    icon: "fas fa-user",
    id: null,
    key: typeKey,
  };

  // Champs applicables
  const applicableFields = useMemo(() => {
    if (!currentType) return [];
    return customFields
      .filter((f) => {
        const ids = f.applicableIds || [];
        return (
          ids.length === 0 ||
          ids.includes(currentType.id) ||
          ids.includes(currentType.key)
        );
      })
      .sort(byOrderThenLabel); // [ok] respecte l'ordre défini dans l'éditeur
  }, [customFields, currentType]);

  // [fire] Charger les relations sauvegardées dans Firestore
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = listenRelationRowsHybrid(
      user.uid,
      typeKey,
      (out) => setRows(out || []),
      { pageSize: 50 }
    ); // [lock] 50 derniers en live
    (async () => {
      const { cursor } = await getRelationRowsPage(user.uid, typeKey, {
        pageSize: 50,
      });
      setRelCursor(cursor);
    })();
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid, typeKey]);
  const merged = useMemo(() => {
    const map = new Map([...rows, ...olderRows].map((r) => [r.id, r]));
    return Array.from(map.values());
  }, [rows, olderRows]);

  // Filtrage simple
  const filtered = useMemo(() => {
    return merged.filter((r) =>
      applicableFields.some((f) =>
        ((r[f.key] ?? "") + "").toLowerCase().includes(q.toLowerCase())
      )
    );
  }, [merged, q, applicableFields]);

  // [down] ordonner après filtrage (utilisé par LISTE + GRILLE)
  const ordered = useMemo(() => {
    return filtered
      .map((r, i) => ({ r, ts: rowToTs(r, i) }))
      .sort((a, b) => (sortOrder === "desc" ? b.ts - a.ts : a.ts - b.ts))
      .map((x) => x.r);
  }, [filtered, sortOrder]);

  // Clé unique pour détecter doublons
  const buildKey = (row) => {
    return applicableFields
      .map((f) => (row[f.key] || "").toString().trim().toLowerCase())
      .join("|");
  };

  const isDateField = (field) => {
    if (!field) return false;
    if (field.type === "date") return true;
    const key = (field.key || "").toLowerCase();
    return /date|jour|birth|naiss|created|updated/.test(key);
  };

  const excelSerialToISO = (serial) => {
    if (typeof serial !== "number" || !Number.isFinite(serial)) return null;
    const parsed =
      XLSX?.SSF && typeof XLSX.SSF.parse_date_code === "function"
        ? XLSX.SSF.parse_date_code(serial)
        : null;
    if (!parsed || !parsed.y || !parsed.m || !parsed.d) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`;
  };

  const normalizeImportedRow = (row) => {
    if (!row || typeof row !== "object") return row;
    const next = { ...row };
    applicableFields.forEach((field) => {
      if (!isDateField(field)) return;
      const raw = next[field.key];
      if (raw == null || raw === "") return;
      if (typeof raw === "number") {
        if (raw > 25569 && raw < 600000) {
          const iso = excelSerialToISO(raw);
          if (iso) next[field.key] = iso;
        }
        return;
      }
      if (typeof raw === "string" && /^\d+(\.\d+)?$/.test(raw)) {
        const num = Number(raw);
        if (Number.isFinite(num) && num > 25569 && num < 600000) {
          const iso = excelSerialToISO(num);
          if (iso) next[field.key] = iso;
        }
      }
    });
    return next;
  };

  // Fusionner sans doublons
  const mergeRows = async (newRows) => {
    if (!user?.uid) return;
    const preparedRows = (Array.isArray(newRows) ? newRows : []).map(
      normalizeImportedRow
    );
    // dédup côté client selon les champs applicables
    const existingKeys = new Set(rows.map(buildKey));
    const toInsert = [];
    preparedRows.forEach((r) => {
      const k = buildKey(r);
      if (!existingKeys.has(k)) {
        toInsert.push(r);
        existingKeys.add(k);
      }
    });
    if (toInsert.length) {
      await addRelationRowsBatch(user.uid, typeKey, toInsert);
    }
  };

  // Ouvrir modale ajout
  const openAddModal = () => {
    setEditingId(null);
    setFormData({});
    setShowModal(true);
  };

  // Ouvrir modale édition
  const openEditModal = (row) => {
    setEditingId(row?.id || null);
    setFormData(row || {});
    setShowModal(true);
  };

  // Ajout / édition
  const onSubmitRelation = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = {};
    applicableFields.forEach((f) => {
      let value = form.get(f.key);
      if (f.type === "checkbox") value = form.get(f.key) ? true : false;
      payload[f.key] = value;
    });
    if (!user?.uid) return;
    if (editingId) {
   await updateRelationRow(user.uid, typeKey, editingId, payload);
    } else {
      await addRelationRow(user.uid, typeKey, payload);
    }
    setShowModal(false);
    setEditingId(null);
  };

  // Suppression
  const removeRow = async (row) => {
   const id = row?.id;
    if (!user?.uid) return;  
    if (id) await deleteRelationRow(user.uid, typeKey, id);
  };
  // Export JSON
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${typeKey}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export CSV
  const exportCSV = () => {
    if (!rows.length) return;
    const headers = applicableFields.map((f) => f.key);
    const csvRows = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => `"${r[h] || ""}"`).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${typeKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import fichier CSV, JSON, XLSX
  const importFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      if (file.name.endsWith(".json")) {
        try {
          const data = JSON.parse(content);
          if (Array.isArray(data)) mergeRows(data);
        } catch {
          alert("Erreur lors de l'import JSON");
        }
      } else if (file.name.endsWith(".csv")) {
        const lines = content.split("\n").filter((l) => l.trim());
        const headers = lines[0]
          .split(",")
          .map((h) => h.replace(/"/g, "").trim());
        const data = lines.slice(1).map((line) => {
          const values = line
            .split(",")
            .map((v) => v.replace(/^"|"$/g, "").trim());
          const obj = {};
          headers.forEach((h, i) => {
            obj[h] = values[i] || "";
          });
          return obj;
        });
        mergeRows(data);
      } else if (file.name.endsWith(".xlsx")) {
        const workbook = XLSX.read(content, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        mergeRows(data);
      }
    };

    if (file.name.endsWith(".xlsx")) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  return (
    <div>
      {/* En-tête */}
      <div className="page-header">
        <h1 className="page-title">{currentType.name || currentType.label}</h1>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> Ajouter{" "}
            {currentType.name?.toLowerCase() || "relation"}
          </button>
          <button className="btn btn-outline" onClick={exportCSV}>
            <i className="fas fa-file-csv"></i> Exporter CSV
          </button>
          <button className="btn btn-outline" onClick={exportJSON}>
            <i className="fas fa-file-code"></i> Exporter JSON
          </button>
          <button
            className="btn btn-outline"
            onClick={() => fileInputRef.current.click()}
          >
            <i className="fas fa-file-import"></i> Importer
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".json,.csv,.xlsx"
            onChange={importFile}
          />
        </div>
      </div>

      {/* Recherche */}
      <div className="filters-section">
        <div className="filters-header">
          <h3 className="filters-title">Recherche</h3>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Texte</label>
            <input
              className="filter-input"
              type="text"
              placeholder={`Rechercher un ${
                currentType.name?.toLowerCase() || "élément"
              }...`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Switch vue */}
      <div className="view-options">
        <div className="view-toggle">
          <button
            className={`view-btn ${view === "list" ? "active" : ""}`}
            onClick={() => setView("list")}
          >
            <i className="fas fa-list"></i>
          </button>
          <button
            className={`view-btn ${view === "grid" ? "active" : ""}`}
            onClick={() => setView("grid")}
          >
            <i className="fas fa-grip-horizontal"></i>
          </button>
        </div>
      </div>

      {/* LISTE */}
      {view === "list" && (
        <div className="table-container">
          <div className="table-header">
            <h3 className="table-title">
              Tous les {currentType.name?.toLowerCase() || "relations"}
            </h3>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label className="filter-label">Tri</label>
              <select
                className="filter-input"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Plus récent</option>
                <option value="asc">Plus ancien</option>
              </select>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                {applicableFields.map((f) => (
                  <th key={f.key}>{safeLabel(f)}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((r, i) => (
                <tr key={i}>
                  {applicableFields.map((f) => (
                    <td key={f.key}>{r[f.key] || "-"}</td>
                  ))}
                  <td>
                    <div className="actions">
                      <button
                        className="action-btn"
                        title="Modifier"
                        onClick={() => openEditModal(r)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="action-btn"
                        title="Supprimer"
                        onClick={() => removeRow(r)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {ordered.length === 0 && (
                <tr>
                  <td
                    colSpan={applicableFields.length + 1}
                    style={{ textAlign: "center", color: "#6b7280" }}
                  >
                    Aucun élément pour ce filtre.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* GRILLE */}
      {view === "grid" && (
        <>
          {/* bandeau de tri */}
          <div
            className="grid-toolbar"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              margin: "8px 0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label className="filter-label">Tri</label>
              <select
                className="filter-input"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Plus récent</option>
                <option value="asc">Plus ancien</option>
              </select>
            </div>
          </div>

          <div className="grid-container">
            {ordered.map((r, i) => (
              <div key={i} className="grid-card">
                <div className="grid-card-header">
                  <i className={currentType.icon}></i>
                  <span>{currentType.name}</span>
                </div>
                <div className="grid-card-body">
                  {applicableFields.map((f) => (
                    <div key={f.key} className="grid-field">
                      <strong>{safeLabel(f)}:</strong> {r[f.key] || "-"}
                    </div>
                  ))}
                </div>
                <div className="grid-card-actions">
                  <button
                    className="action-btn"
                    title="Modifier"
                    onClick={() => openEditModal(i)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    className="action-btn"
                    title="Supprimer"
                    onClick={() => removeRow(i)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
            {ordered.length === 0 && (
              <div
                className="grid-empty"
                style={{ textAlign: "center", color: "#6b7280", width: "100%" }}
              >
                Aucun élément pour ce filtre.
              </div>
            )}
          </div>
        </>
      )}
      {relCursor && (
        <div style={{ textAlign: "center", margin: "12px 0" }}>
          <button
            className="btn btn-outline"
            disabled={isMoreRelLoading}
            onClick={loadMoreRelations}
          >
            {isMoreRelLoading ? "Chargement..." : "Voir plus"}
          </button>
        </div>
      )}
      {/* MODALE AJOUT/ÉDITION */}
      {showModal && (
        <div
          className="formula-helper-overlay show"
          onClick={(e) => {
            if (e.target.classList.contains("formula-helper-overlay"))
              setShowModal(false);
          }}
        >
          <div
            className="formula-helper-modal full-page-form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="formula-helper-header">
              <h3>
                <i className="fas fa-user-edit"></i>
                {editingId !== null ? "Modifier" : "Ajouter"} un{" "}
                {currentType.name?.toLowerCase() || "élément"}
              </h3>
              <button
                className="formula-close-btn"
                onClick={() => setShowModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="formula-helper-content full-page-content">
              <form onSubmit={onSubmitRelation} className="full-page-form-grid">
                {applicableFields.map((f) => (
                  <div
                    key={f.key}
                    className={`form-group ${
                      f.type === "textarea" ? "full-width" : ""
                    }`}
                  >
                    <label className="form-label full-page-label">
                      {safeLabel(f)}
                    </label>

                    {f.type === "text" && (
                      <input
                        name={f.key}
                        type="text"
                        className="form-input"
                        defaultValue={formData[f.key] || ""}
                      />
                    )}
                    {f.type === "number" && (
                      <input
                        name={f.key}
                        type="number"
                        className="form-input"
                        defaultValue={formData[f.key] || ""}
                      />
                    )}
                    {f.type === "email" && (
                      <input
                        name={f.key}
                        type="email"
                        className="form-input"
                        defaultValue={formData[f.key] || ""}
                      />
                    )}
                    {f.type === "phone" && (
                      <input
                        name={f.key}
                        type="tel"
                        className="form-input"
                        defaultValue={formData[f.key] || ""}
                      />
                    )}
                    {f.type === "date" && (
                      <input
                        name={f.key}
                        type="date"
                        className="form-input"
                        defaultValue={formData[f.key] || ""}
                      />
                    )}
                    {f.type === "textarea" && (
                      <textarea
                        name={f.key}
                        className="form-input full-page-textarea"
                        defaultValue={formData[f.key] || ""}
                        rows={6}
                      />
                    )}
                    {f.type === "select" && (
                      <select
                        name={f.key}
                        className="form-select"
                        defaultValue={formData[f.key] || ""}
                      >
                        <option value=""></option>
                        {(f.options || "")
                          .split(/[;,]/g)
                          .filter(Boolean)
                          .map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                      </select>
                    )}
                    {f.type === "checkbox" && (
                      <label className="checkbox-label full-page-checkbox">
                        <input
                          name={f.key}
                          type="checkbox"
                          defaultChecked={formData[f.key] || false}
                        />
                        <span>Coché</span>
                      </label>
                    )}
                  </div>
                ))}

                <div className="modal-footer full-page-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    <i className="fas fa-times"></i> Annuler
                  </button>
                  <button className="btn btn-primary" type="submit">
                    <i className="fas fa-save"></i> Enregistrer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
